import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { DisableRightClickService } from './modules/service/disable-right-click.service';

const INSTALL_DISMISSED_KEY = 'pwa-install-dismissed';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'smcportal';

  // ── Banners ────────────────────────────────────────────────────────────────
  showUpdateBanner  = false;
  showOfflineBanner = false;
  showInstallCard   = false;

  /** true = iOS Safari (needs manual share-sheet instructions) */
  isIos = false;
  /** true = Chrome/Edge/Android (native install button available) */
  canNativeInstall = false;

  private deferredInstallPrompt: any = null;
  private installPromptTimer: any    = null;

  private swUpdateHandler    = () => { this.showUpdateBanner = true; };
  private onlineHandler      = () => { this.showOfflineBanner = false; };
  private offlineHandler     = () => { this.showOfflineBanner = true; };
  private beforeInstallHandler = (e: any) => {
    e.preventDefault();
    this.deferredInstallPrompt = e;
    this.canNativeInstall = true;
    // If the delayed timer hasn't fired yet, let it handle showing the card.
    // If it already fired and card isn't visible yet, show it now.
    if (!this.showInstallCard && !this.wasDismissed()) {
      this.showInstallCard = true;
    }
  };

  constructor(private rightClickDisable: DisableRightClickService) {}

  ngOnInit() {
    // ── Offline / online detection ───────────────────────────────────────────
    this.showOfflineBanner = !navigator.onLine;
    window.addEventListener('online',  this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);

    // ── SW update detection ──────────────────────────────────────────────────
    window.addEventListener('sw-update-available', this.swUpdateHandler);

    // ── PWA Install ──────────────────────────────────────────────────────────
    // Don't show if already running installed (standalone mode)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    if (isStandalone || this.wasDismissed()) { return; }

    // Detect iOS Safari
    const ua = window.navigator.userAgent.toLowerCase();
    this.isIos = /iphone|ipad|ipod/.test(ua) && !(window as any).MSStream;

    // Capture the beforeinstallprompt for Chrome/Android
    window.addEventListener('beforeinstallprompt', this.beforeInstallHandler);

    // Show the install card after 4 seconds on load
    this.installPromptTimer = setTimeout(() => {
      if (this.isIos || this.deferredInstallPrompt) {
        this.showInstallCard = true;
      }
    }, 4000);
  }

  ngOnDestroy() {
    window.removeEventListener('online',  this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
    window.removeEventListener('sw-update-available', this.swUpdateHandler);
    window.removeEventListener('beforeinstallprompt', this.beforeInstallHandler);
    if (this.installPromptTimer) { clearTimeout(this.installPromptTimer); }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  private wasDismissed(): boolean {
    return localStorage.getItem(INSTALL_DISMISSED_KEY) === '1';
  }

  // ── Update banner actions ──────────────────────────────────────────────────
  applyUpdate() {
    this.showUpdateBanner = false;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg && reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    }
  }

  dismissUpdate() {
    this.showUpdateBanner = false;
  }

  // ── Install card actions ───────────────────────────────────────────────────
  installApp() {
    if (this.deferredInstallPrompt) {
      this.deferredInstallPrompt.prompt();
      this.deferredInstallPrompt.userChoice.then((choice: any) => {
        this.deferredInstallPrompt = null;
        this.showInstallCard = false;
        if (choice.outcome === 'accepted') {
          localStorage.setItem(INSTALL_DISMISSED_KEY, '1');
        }
      });
    }
  }

  dismissInstall() {
    this.showInstallCard = false;
    localStorage.setItem(INSTALL_DISMISSED_KEY, '1');
  }

  @HostListener('window:appinstalled')
  onAppInstalled() {
    this.showInstallCard = false;
    this.deferredInstallPrompt = null;
    localStorage.setItem(INSTALL_DISMISSED_KEY, '1');
  }
}


